import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Control, FieldValues, Path, RegisterOptions } from 'react-hook-form';

import type { ChipColor } from '@/components/fuel-autocomplete/FuelAutoComplete';
import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import { pickFieldHelpProps } from '@/components/VNFieldHelp/normalizeFieldHelpProps';

import { AutocompleteContext, AutocompleteDisplay, AutocompleteList } from './_autocompleteSlots';
import type {
  DisplaySlotProps,
  DropdownOption,
  DropdownOptionInput,
  FilterOption,
  ListSlotProps,
  WithMinSelections,
} from './types';
import { normalizeDropdownOptions } from './types';
import VNDropdownBase from './VNDropdownBase';
import VNDropdownRhf from './VNDropdownRhf';

function getLiveDelay(query: string): number {
  if (query.length === 0) return 0;
  if (query.length === 1) return 2000;
  if (query.length === 2) return 1000;
  if (query.length === 3) return 500;
  return 250;
}

// ─── Shared props ─────────────────────────────────────────────────────────────

interface SharedProps extends VNFieldHelpProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  displayComponent?: React.ComponentType<DisplaySlotProps>;
  listComponent?: React.ComponentType<ListSlotProps>;
  disableBorder?: boolean;
  compact?: boolean;
  popoverWidth?: number;
  popoverMinHeight?: number;
  className?: string;
  searchable?: 'autocomplete' | 'inset' | 'live';
  filterOptions?: FilterOption[];
  liveSearchFn?: (query: string) => Promise<DropdownOptionInput[]>;
  /**
   * Minimum number of options that must remain selected (multi-select only).
   * When all chips are removed the first N options are automatically restored
   * after a short delay.
   *
   * Use this when `listComponent` is a closure (e.g. wrapping `VNDropdownOUOptions`
   * to pass the `link` prop), because closure functions cannot carry the static
   * `minSelections` marker that component slots normally use for auto-detection.
   *
   * @example
   * // Without link — auto-detected from VNDropdownOUOptions.minSelections
   * listComponent={VNDropdownOUOptions}
   *
   * // With link — must be passed explicitly
   * listComponent={(props) => <VNDropdownOUOptions {...props} link={goToOU} />}
   * minSelections={1}
   */
  minSelections?: number;
  /**
   * When true (default), honor `minSelections` / list-slot markers that auto-select
   * the current OU (or first N options) when the selection is empty.
   * Set to false to allow a fully empty selection (e.g. draft Audience with no OUs).
   */
  requireSelection?: boolean;
  /**
   * Fallback options for display of pre-selected values that may not be in the live-search
   * results yet. Passed through to VNDropdownBase as `seedOptions`.
   */
  seedOptions?: DropdownOptionInput[];
  /**
   * When true, render a small clear button inside the trigger (left of the caret) that
   * wipes the entire selection in one click. Visible only when at least one value is selected.
   * Per-chip delete in multi-select still works.
   */
  allowClear?: boolean;
  /** Named chip color. `category` maps to `.tag-chip.cat` (#6897b1). */
  chipColor?: ChipColor;
  required?: boolean;
  /** Allow a typed value that is not in the option list (single-select autocomplete). */
  allowCustom?: boolean;
  /** Hide the option list and keep the search input visible (tag entry). */
  hideOptions?: boolean;
  optionTemplate?: (option: DropdownOption) => React.ReactNode;
  inputValue?: string;
  onInputChange?: (event: React.SyntheticEvent | null, value: string, reason: string) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** Intercept selection (e.g. tag commit). When set, the form field is not updated by the dropdown. */
  onValueChange?: (value: DropdownOption['id'] | Array<DropdownOption['id']> | null) => void;
  /** RHF mode: runs after the field blur handler when the list closes. */
  onAfterBlur?: () => void;
  loading?: boolean;
  showLoadingFooter?: boolean;
  loadingFooterText?: string;
  /** Portal the listbox to `document.body` so it stays with the trigger on scroll. Default true. */
  usePortal?: boolean;
}

// ─── RHF mode — name + control ────────────────────────────────────────────────

interface VNDropdownRhfControlMode<TFieldValues extends FieldValues = FieldValues> extends SharedProps {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  value?: never;
  onChange?: never;
  onBlur?: never;
  error?: never;
}

// ─── RHF mode — name only (FormProvider) ──────────────────────────────────────

interface VNDropdownRhfContextMode<TFieldValues extends FieldValues = FieldValues> extends SharedProps {
  name: Path<TFieldValues>;
  control?: never;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  value?: never;
  onChange?: never;
  onBlur?: never;
  error?: never;
}

// ─── Standalone controlled mode — value + onChange ────────────────────────────

interface VNDropdownStandaloneMode extends SharedProps {
  name?: never;
  control?: never;
  rules?: never;
  value: DropdownOption['id'] | Array<DropdownOption['id']> | null;
  onChange: (value: DropdownOption['id'] | Array<DropdownOption['id']> | null) => void;
  onBlur?: () => void;
  error?: string;
}

export type VNDropdownProps<TFieldValues extends FieldValues = FieldValues> =
  | VNDropdownRhfControlMode<TFieldValues>
  | VNDropdownRhfContextMode<TFieldValues>
  | VNDropdownStandaloneMode;

const VNDropdown = <TFieldValues extends FieldValues = FieldValues>(props: VNDropdownProps<TFieldValues>) => {
  const [query, setQuery] = useState(props.inputValue ?? '');
  const selectFocusedRef = useRef<(() => void) | null>(null);

  const [liveOptions, setLiveOptions] = useState<DropdownOption[]>([]);
  const [isLiveLoading, setIsLiveLoading] = useState(false);
  const liveRequestIdRef = useRef(0);
  const liveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    options,
    label,
    placeholder,
    disabled,
    multiple,
    displayComponent,
    listComponent,
    disableBorder,
    searchable,
    compact,
    popoverWidth,
    popoverMinHeight,
    className,
    filterOptions,
    liveSearchFn,
    minSelections: minSelectionsProp,
    requireSelection = true,
    seedOptions,
    allowClear,
    chipColor,
    required,
    allowCustom,
    hideOptions,
    optionTemplate,
    inputValue,
    onInputChange,
    inputProps,
    onValueChange,
    onAfterBlur,
    loading,
    showLoadingFooter,
    loadingFooterText,
    usePortal = true,
  } = props;

  const fieldHelpProps = pickFieldHelpProps(props);
  const useAutocompleteSlots = searchable === 'autocomplete' || searchable === 'live' || hideOptions;
  const resolvedDisplay = useAutocompleteSlots ? AutocompleteDisplay : displayComponent;
  const resolvedList = useAutocompleteSlots ? AutocompleteList : listComponent;

  // Prefer the explicit prop; fall back to the static marker on the list component.
  // The fallback is only reliable when listComponent is the component itself (not a closure).
  // requireSelection=false disables auto-restore even when VNDropdownOUOptions.minSelections is set.
  const minSelections = requireSelection
    ? minSelectionsProp ?? (listComponent as React.ComponentType<ListSlotProps> & WithMinSelections)?.minSelections
    : 0;

  const setQueryAndNotify = (next: string, reason = 'input') => {
    setQuery(next);
    onInputChange?.(null, next, reason);
  };

  const contextValue = useMemo(
    () => ({
      query: inputValue !== undefined ? inputValue : query,
      setQuery: setQueryAndNotify,
      selectFocusedRef,
      listComponent,
    }),
    [query, inputValue, listComponent, onInputChange]
  );

  useEffect(() => {
    if (inputValue !== undefined) setQuery(inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (!liveSearchFn) return undefined;
    if (liveDebounceRef.current !== null) {
      clearTimeout(liveDebounceRef.current);
      liveDebounceRef.current = null;
    }
    const requestId = ++liveRequestIdRef.current;
    const delay = getLiveDelay(query);
    // Show spinner immediately — even while waiting for the debounce delay.
    setIsLiveLoading(true);
    const execute = async () => {
      liveDebounceRef.current = null;
      try {
        const results = await liveSearchFn(query);
        if (liveRequestIdRef.current === requestId) setLiveOptions(normalizeDropdownOptions(results));
      } catch {
        // silently ignore
      } finally {
        if (liveRequestIdRef.current === requestId) setIsLiveLoading(false);
      }
    };
    if (delay === 0) {
      execute();
    } else {
      liveDebounceRef.current = setTimeout(execute, delay);
    }
    return () => {
      if (liveDebounceRef.current !== null) {
        clearTimeout(liveDebounceRef.current);
        liveDebounceRef.current = null;
      }
    };
  }, [query, liveSearchFn]);

  const normalizedOptions = useMemo(
    () => (liveSearchFn ? liveOptions : normalizeDropdownOptions(options as DropdownOptionInput[])),
    [options, liveOptions, liveSearchFn]
  );
  const normalizedSeedOptions = useMemo(
    () => normalizeDropdownOptions(seedOptions as DropdownOptionInput[] | undefined),
    [seedOptions]
  );

  const sharedSlotProps = useMemo(
    () => ({
      options: hideOptions ? [] : normalizedOptions,
      label,
      placeholder,
      disabled,
      multiple,
      displayComponent: resolvedDisplay,
      listComponent: resolvedList,
      disableBorder,
      compact,
      popoverWidth,
      popoverMinHeight,
      className,
      insetSearch: searchable === 'inset',
      minSelections,
      filterOptions,
      isLiveLoading: liveSearchFn ? isLiveLoading : Boolean(loading),
      seedOptions: normalizedSeedOptions,
      allowClear,
      chipColor,
      allowCustom,
      hideOptions,
      optionTemplate,
      loadingFooterText: showLoadingFooter ? loadingFooterText : undefined,
      inputProps,
      usePortal,
      ...fieldHelpProps,
    }),
    [
      normalizedOptions,
      hideOptions,
      isLiveLoading,
      liveSearchFn,
      loading,
      showLoadingFooter,
      label,
      placeholder,
      disabled,
      multiple,
      resolvedDisplay,
      resolvedList,
      disableBorder,
      compact,
      popoverWidth,
      popoverMinHeight,
      className,
      searchable,
      minSelections,
      filterOptions,
      normalizedSeedOptions,
      allowClear,
      chipColor,
      allowCustom,
      optionTemplate,
      loadingFooterText,
      inputProps,
      usePortal,
      fieldHelpProps,
    ]
  );

  const isRhf = 'name' in props && props.name !== undefined;

  const inner = isRhf ? (
    <VNDropdownRhf
      {...sharedSlotProps}
      name={(props as VNDropdownRhfControlMode<TFieldValues>).name}
      control={(props as VNDropdownRhfControlMode<TFieldValues>).control}
      rules={(props as VNDropdownRhfControlMode<TFieldValues>).rules}
      required={required}
      onValueChange={onValueChange}
      onAfterBlur={onAfterBlur}
    />
  ) : (
    <VNDropdownBase
      {...sharedSlotProps}
      value={(props as VNDropdownStandaloneMode).value}
      onChange={(props as VNDropdownStandaloneMode).onChange}
      onBlur={(props as VNDropdownStandaloneMode).onBlur}
      error={(props as VNDropdownStandaloneMode).error}
    />
  );

  return useAutocompleteSlots ? (
    <AutocompleteContext.Provider value={contextValue}>{inner}</AutocompleteContext.Provider>
  ) : (
    inner
  );
};

export default VNDropdown;
